import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../App';
import axios from 'axios';

// --- Material-UI Imports ---
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Alert,
    Stack,
    CircularProgress,
    Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, DialogContentText,
    InputAdornment, // Importar para el icono de búsqueda
    Divider, // Importar Divider
    Chip, // Importar Chip
    Autocomplete // Se usa Autocomplete para el nuevo selector
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search'; // Importar icono de búsqueda
import TrafficIcon from '@mui/icons-material/Traffic'; // <-- ÍCONO AÑADIDO
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTheme } from '@mui/material/styles';

// --- CONSTANTES Y TIPOS ---

interface ParamsTemplate { [key: string]: string; }

// --- CAMBIO: La interfaz ahora solo necesita una referencia a la conexión ---
interface TrafficSource {
    id: number;
    name: string;
    postback_url?: string;
    incoming_click_params_map: ParamsTemplate;
    parameter_placeholders: ParamsTemplate;
    api_connection_id?: number | null; // <-- CAMBIO
    api_connection_name?: string; // Para mostrar en la tabla
    created_at: string;
    updated_at: string;
}

interface ApiConnection {
    id: number;
    name: string;
}

type KeyValuePair = { id: number; key: string; value: string; };

const PLACEHOLDER_KEYS = ['clickid', 'cost', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5', 'sub6', 'sub7', 'sub8', 'sub9', 'sub10'];

// --- COMPONENTE PRINCIPAL ---

const TrafficSourcesPage: React.FC = () => {
    // --- ESTADOS ---
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const [apiConnections, setApiConnections] = useState<ApiConnection[]>([]); // <-- NUEVO
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Estados del formulario
    const [name, setName] = useState<string>('');
    const [postbackUrl, setPostbackUrl] = useState<string>('');
    const [placeholders, setPlaceholders] = useState<KeyValuePair[]>([]);
    const [apiConnectionId, setApiConnectionId] = useState<number | ''>(''); // <-- NUEVO

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [idToDelete, setIdToDelete] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const theme = useTheme();

    // --- LÓGICA DE DATOS (FETCHING) ---
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // --- CAMBIO: Se obtienen también las conexiones de API ---
            const [tsResponse, connectionsResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/traffic-sources`),
                axios.get(`${API_BASE_URL}/connections`)
            ]);
            setTrafficSources(tsResponse.data);
            setApiConnections(connectionsResponse.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al cargar los datos.');
            console.error("Error fetching data:", err);
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al cargar los datos.' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- MANEJADORES PARA FORMULARIOS DINÁMICOS ---
    const handleMapChange = (id: number, field: 'key' | 'value', newValue: string) => {
        setPlaceholders(prev => prev.map(item => item.id === id ? { ...item, [field]: newValue } : item));
    };

    const addMapRow = () => {
        setPlaceholders(prev => [...prev, { id: Date.now(), key: '', value: '' }]);
    };

    const removeMapRow = (id: number) => {
        setPlaceholders(prev => prev.filter(item => item.id !== id));
    };

    // --- MANEJADORES DEL FORMULARIO PRINCIPAL ---
    const resetForm = () => {
        setName('');
        setPostbackUrl('');
        setPlaceholders([]);
        setApiConnectionId(''); // <-- CAMBIO
        setEditingId(null);
    };

    const startEditing = (source: TrafficSource) => {
        setNotification(null);
        setEditingId(source.id);
        setName(source.name);
        setPostbackUrl(source.postback_url || '');
        const placeholderArray = Object.entries(source.parameter_placeholders || {}).map(([key, value], index) => ({ id: Date.now() + index + 1000, key, value }));
        setPlaceholders(placeholderArray);
        setApiConnectionId(source.api_connection_id || ''); // <-- CAMBIO
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    const handleClone = (sourceToClone: TrafficSource) => {
        setNotification(null);
        setEditingId(null);
        setName(`${sourceToClone.name} (Copia)`);
        setPostbackUrl(sourceToClone.postback_url || '');
        const placeholderArray = Object.entries(sourceToClone.parameter_placeholders || {}).map(([key, value], index) => ({ id: Date.now() + index, key, value }));
        setPlaceholders(placeholderArray);
        setApiConnectionId(sourceToClone.api_connection_id || ''); // <-- CAMBIO
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setNotification({ type: 'success', message: `Clonando '${sourceToClone.name}'. Revisa los detalles y crea la nueva fuente.` });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setNotification(null);

        if (!name.trim()) {
            setNotification({ type: 'error', message: "El nombre de la fuente de tráfico es obligatorio." });
            return;
        }

        setIsLoading(true);
        const parameter_placeholders = placeholders.reduce((acc, item) => {
            if (item.key.trim() && item.value.trim()) acc[item.key] = item.value;
            return acc;
        }, {} as ParamsTemplate);

        const trafficSourceData = { 
            name, 
            postback_url: postbackUrl || undefined, 
            parameter_placeholders,
            api_connection_id: apiConnectionId || null, // <-- CAMBIO
        };
        const endpoint = editingId ? `/traffic-sources/${editingId}` : '/traffic-sources';
        const method = editingId ? 'PUT' : 'POST';

        try {
            await axios({ method, url: `${API_BASE_URL}${endpoint}`, data: trafficSourceData });
            setNotification({ type: 'success', message: `Fuente de Tráfico ${editingId ? 'actualizada' : 'creada'} con éxito!` });
            resetForm();
            await fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al guardar.' });
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDelete = (id: number) => {
        setIdToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (idToDelete === null) return;
        setIsLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/traffic-sources/${idToDelete}`);
            setNotification({ type: 'success', message: `Fuente de Tráfico (ID: ${idToDelete}) eliminada.` });
            if (editingId === idToDelete) resetForm();
            await fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'No se pudo eliminar la fuente de tráfico.' });
        } finally {
            setIsLoading(false);
            setDeleteConfirmOpen(false);
            setIdToDelete(null);
        }
    };

    const filteredTrafficSources = useMemo(() => {
        if (!searchTerm) {
            return trafficSources;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return trafficSources.filter(source =>
            String(source.id).includes(lowerCaseSearchTerm) ||
            source.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            (source.postback_url || '').toLowerCase().includes(lowerCaseSearchTerm) ||
            Object.values(source.parameter_placeholders).some(val => val.toLowerCase().includes(lowerCaseSearchTerm))
        );
    }, [trafficSources, searchTerm]);

    if (isLoading && trafficSources.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error && trafficSources.length === 0) return <Alert severity="error" sx={{ m: 3 }}>Error: {error}</Alert>;

    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <TrafficIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Fuentes de Tráfico
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    {editingId ? 'Editar Fuente de Tráfico' : 'Crear Nueva Fuente de Tráfico'}
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                            <TextField fullWidth label="Nombre de la Fuente" value={name} onChange={e => setName(e.target.value)} required />
                            <TextField fullWidth label="URL Postback (opcional)" value={postbackUrl} onChange={e => setPostbackUrl(e.target.value)} placeholder="https://postback.server.com?cid={external_id}" />
                        </Stack>

                        <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>Diccionario de Placeholders de URL</Typography>
                            <Stack spacing={1.5}>
                                {placeholders.map((item) => (
                                    <Stack direction="row" spacing={1} key={item.id} alignItems="center">
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Parámetro Tracker</InputLabel>
                                            <Select label="Parámetro Tracker" value={item.key} onChange={e => handleMapChange(item.id, 'key', e.target.value)}>
                                                <MenuItem value=""><em>Seleccionar</em></MenuItem>
                                                {PLACEHOLDER_KEYS.map(pk => <MenuItem key={pk} value={pk}>{pk}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <Typography>➡️</Typography>
                                        <TextField fullWidth size="small" placeholder="Placeholder Fuente (ej. {zone_id})" value={item.value} onChange={e => handleMapChange(item.id, 'value', e.target.value)} />
                                        <Tooltip title="Eliminar Placeholder">
                                            <IconButton aria-label="Eliminar Placeholder" size="small" color="error" onClick={() => removeMapRow(item.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                ))}
                            </Stack>
                            <Button startIcon={<AddCircleOutlineIcon />} onClick={addMapRow} sx={{ mt: 2 }} variant="outlined">Añadir Placeholder</Button>
                        </Box>
                        
                        <Divider>Configuración del Optimizador</Divider>
                        
                        {/* --- CAMBIO: Nuevo selector para las conexiones de API --- */}
                        <Autocomplete
                            fullWidth
                            options={apiConnections}
                            getOptionLabel={(option) => `(${option.id}) ${option.name}`}
                            value={apiConnections.find(c => c.id === apiConnectionId) || null}
                            onChange={(event, newValue) => {
                                setApiConnectionId(newValue ? newValue.id : '');
                            }}
                            renderInput={(params) => <TextField {...params} label="Conexión de API (Opcional)" helperText="Selecciona una conexión pre-configurada desde el Gestor de Conexiones."/>}
                        />

                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button type="submit" variant="contained" color="primary" disabled={isLoading}>{editingId ? 'Actualizar Fuente' : 'Crear Fuente'}</Button>
                            {editingId && <Button variant="text" onClick={resetForm} sx={{ ml: 2 }}>Cancelar</Button>}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary">Fuentes de Tráfico Registradas</Typography>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar por ID, Nombre o URL Postback..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                />
            </Stack>
            <Paper elevation={3} sx={{ overflowX: 'auto', borderRadius: '12px' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>ID</TableCell>
                                <TableCell sx={headerCellStyle}>Nombre</TableCell>
                                <TableCell sx={headerCellStyle}>URL Postback</TableCell>
                                <TableCell sx={headerCellStyle}>Conexión API</TableCell> {/* <-- CAMBIO */}
                                <TableCell sx={headerCellStyle}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTrafficSources.map((source) => (
                                <TableRow key={source.id} hover>
                                    <TableCell>{source.id}</TableCell>
                                    <TableCell>{source.name}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-all', fontSize: '0.875rem', color: 'text.secondary' }}>
                                        {source.postback_url || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        {source.api_connection_name ? <Chip label={source.api_connection_name} size="small" /> : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Clonar Fuente">
                                                <IconButton size="small" color="default" onClick={() => handleClone(source)}>
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar Fuente">
                                                <IconButton size="small" color="primary" onClick={() => startEditing(source)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar Fuente">
                                                <IconButton size="small" color="error" onClick={() => confirmDelete(source.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>{"Confirmar Eliminación"}</DialogTitle>
                <DialogContent>
                    <DialogContentText color="text.secondary">
                        ¿Estás seguro de que quieres eliminar esta fuente de tráfico? Esta acción es irreversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" autoFocus>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TrafficSourcesPage;
